/**
 * AtlasChatPage — full-page conversational interface (ChatGPT/Claude-style).
 *
 * - Left sidebar (~260px): conversation list + "New chat" button
 * - Right main area: scrollable message history (top) + input bar (bottom)
 * - Non-streaming in v1.0; streaming lands in v1.1
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import { AtlasApprovalsList } from './AtlasApprovalsPage';
import {
  Send, Loader2, Plus, Archive, MessageSquare, Sparkles, Clock, ClipboardCheck, SlidersHorizontal,
  PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const createViewKey = (prefix) => `${prefix}-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`}`;

const SUGGESTED_PROMPTS = [
  "What's my morning briefing?",
  "Show me my top 5 missions by deadline.",
  "Draft a CO email for my most urgent opportunity.",
  "Which of my missions has a compliance gap?",
];

export const AtlasChatInterface = ({ embedded = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [viewKey, setViewKey] = useState(() => createViewKey('draft'));
  const [pendingViews, setPendingViews] = useState(() => new Set());
  const [loadingConv, setLoadingConv] = useState(false);
  const [approvalCount, setApprovalCount] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(() => window.innerWidth >= 768);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const activeViewRef = useRef(viewKey);
  const sending = pendingViews.has(viewKey);
  const activeTab = searchParams.get('tab') === 'approvals' ? 'approvals' : 'chat';

  const activateView = useCallback((key) => {
    activeViewRef.current = key;
    setViewKey(key);
  }, []);

  const updateConversationUrl = useCallback((id) => {
    if (embedded) return;
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      if (id) next.set('c', id);
      else next.delete('c');
      return next;
    });
  }, [embedded, setSearchParams]);

  const loadConvList = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/v1/atlas/conversations`, { headers: auth() });
      if (r.ok) { const d = await r.json(); setConversations(d.conversations || []); }
    } catch {}
  }, []);

  const loadConv = useCallback(async (id) => {
    if (!id) { setMessages([]); setActiveId(null); updateConversationUrl(null); return; }
    const nextViewKey = `conversation-${id}`;
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.delete('tab');
      return next;
    });
    activateView(nextViewKey);
    setLoadingConv(true);
    try {
      const r = await fetch(`${API_URL}/api/v1/atlas/conversations/${id}`, { headers: auth() });
      if (r.ok) {
        const d = await r.json();
        if (activeViewRef.current === nextViewKey) {
          setMessages(d.messages || []);
          setActiveId(d.id);
          updateConversationUrl(d.id);
        }
      }
    } catch {}
    if (activeViewRef.current === nextViewKey) setLoadingConv(false);
  }, [activateView, setSearchParams, updateConversationUrl]);

  useEffect(() => { loadConvList(); }, [loadConvList]);

  useEffect(() => {
    const syncCloudHistory = async () => {
      loadConvList();
      if (!activeId || sending) return;
      try {
        const response = await fetch(`${API_URL}/api/v1/atlas/conversations/${activeId}`, { headers: auth() });
        if (!response.ok) return;
        const conversation = await response.json();
        if (activeViewRef.current === `conversation-${activeId}`) {
          setMessages(conversation.messages || []);
        }
      } catch {}
    };
    const interval = setInterval(syncCloudHistory, 15000);
    const onFocus = () => syncCloudHistory();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [activeId, loadConvList, sending]);

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/v1/atlas/pending-approvals`, { headers: auth() })
      .then(response => response.ok ? response.json() : null)
      .then(data => { if (active && data) setApprovalCount((data.approvals || []).length); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    // If URL has ?c=<id> on mount, load that conversation
    const id = searchParams.get('c');
    if (!embedded && id) loadConv(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  useEffect(() => {
    // Auto-grow textarea up to 6 lines
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 160) + 'px';
    }
  }, [input]);

  const sendMessage = async (overrideText = null) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;
    const requestViewKey = viewKey;
    const requestConversationId = activeId;
    setPendingViews(previous => new Set(previous).add(requestViewKey));
    setMessages(m => [...m, { role: 'user', content: text, timestamp: new Date().toISOString() }]);
    setInput('');
    try {
      const r = await fetch(`${API_URL}/api/v1/atlas/chat`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, conversation_id: requestConversationId }),
      });
      if (!r.ok) {
        const err = await r.text();
        toast.error(`Atlas: ${err.slice(0, 240)}`);
        if (activeViewRef.current === requestViewKey) {
          setMessages(m => [...m, { role: 'assistant', content: `(error: ${err.slice(0, 400)})`, timestamp: new Date().toISOString() }]);
        }
      } else {
        const d = await r.json();
        if (activeViewRef.current === requestViewKey) {
          if (!requestConversationId) {
            setActiveId(d.conversation_id);
            activateView(`conversation-${d.conversation_id}`);
            updateConversationUrl(d.conversation_id);
          }
          setMessages(m => [...m, { role: 'assistant', content: d.reply || '(no reply)', timestamp: new Date().toISOString() }]);
        }
        loadConvList();
      }
    } catch (e) { toast.error('Network error'); }
    setPendingViews(previous => {
      const next = new Set(previous);
      next.delete(requestViewKey);
      return next;
    });
  };

  const newConv = () => {
    activateView(createViewKey('draft'));
    setActiveId(null); setMessages([]); setInput(''); setLoadingConv(false); updateConversationUrl(null);
    textareaRef.current?.focus();
  };

  const openApprovals = () => {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.set('tab', 'approvals');
      next.delete('c');
      return next;
    });
  };

  const archiveConv = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Archive this conversation?')) return;
    try {
      const response = await fetch(`${API_URL}/api/v1/atlas/conversations/${id}`, { method: 'DELETE', headers: auth() });
      if (!response.ok) throw new Error('Archive failed');
      if (id === activeId) newConv();
      await loadConvList();
      toast.success('Conversation archived');
    } catch {
      toast.error('Could not archive this conversation');
    }
  };

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      const today = new Date();
      if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  const content = (
      <div
        className="bsiq-atlas-workspace flex h-full min-h-0 overflow-hidden bg-[var(--bsiq-surface-default)]"
        data-testid="atlas-chat-page"
      >
        {/* LEFT: conversation list */}
        <aside className={`${historyOpen ? 'flex' : 'hidden'} absolute inset-y-0 left-0 z-30 w-[min(20rem,88vw)] flex-col border-r border-[var(--bsiq-surface-border)] bg-white md:static md:z-auto md:w-64`} data-testid="atlas-conv-list">
          <div className="p-3" style={{ borderBottom: '1px solid #E5E7EB' }}>
            <button
              onClick={newConv}
              data-testid="atlas-new-conv"
              className="bsiq-focus-ring inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg py-2 text-sm font-bold transition-colors"
              style={{ backgroundColor: '#C49A00', color: '#0D1F3C' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#A8840A')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#C49A00')}
            >
              <Plus className="w-4 h-4" /> New chat
            </button>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={openApprovals}
                aria-pressed={activeTab === 'approvals'}
                className={`relative inline-flex min-h-10 items-center justify-center gap-1.5 rounded-lg border px-2 text-[11px] font-bold transition-colors ${activeTab === 'approvals' ? 'border-[#0D1F3C] bg-[#0D1F3C] text-white' : 'border-slate-200 bg-white text-[#0D1F3C] hover:bg-slate-50'}`}
                data-testid="atlas-review-queue"
              >
                <ClipboardCheck className="h-3.5 w-3.5" aria-hidden="true" /> Review
                {approvalCount > 0 && <span className="rounded-full bg-[#C49A00] px-1.5 py-0.5 text-[10px] text-[#0D1F3C]">{approvalCount}</span>}
              </button>
              <button
                type="button"
                onClick={() => navigate('/settings/atlas/autonomy')}
                className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 text-[11px] font-bold text-[#0D1F3C] transition-colors hover:bg-slate-50"
                data-testid="atlas-autonomy-settings"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" /> Autonomy
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 && (
              <div className="p-4 text-[10px] italic text-center" style={{ color: '#9CA3AF' }}>No conversations yet</div>
            )}
            {conversations.map(c => (
              <div
                key={c.id}
                onClick={() => { loadConv(c.id); if (window.innerWidth < 768) setHistoryOpen(false); }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    loadConv(c.id);
                    if (window.innerWidth < 768) setHistoryOpen(false);
                  }
                }}
                role="button"
                tabIndex={0}
                data-testid={`conv-item-${c.id}`}
                className="group relative px-3 py-2.5 cursor-pointer transition-colors"
                style={c.id === activeId
                  ? { backgroundColor: '#FEF3C7', borderLeft: '3px solid #C49A00', paddingLeft: '9px', borderBottom: '1px solid #F3F4F6' }
                  : { borderLeft: '3px solid transparent', borderBottom: '1px solid #F3F4F6' }
                }
                onMouseEnter={(e) => { if (c.id !== activeId) e.currentTarget.style.backgroundColor = '#F0EDE6'; }}
                onMouseLeave={(e) => { if (c.id !== activeId) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <div className="text-xs font-semibold truncate" style={{ color: c.id === activeId ? '#0D1F3C' : '#374151' }}>
                  {c.title || 'New conversation'}
                </div>
                <div className="flex items-center gap-1.5 mt-1 text-[10px]" style={{ color: '#9CA3AF' }}>
                  <Clock className="w-2.5 h-2.5" />
                  {fmtDate(c.last_activity_at || c.created_at)}
                </div>
                <button
                  onClick={(e) => archiveConv(c.id, e)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 opacity-60 transition hover:bg-red-50 hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-[#C49A00]"
                  style={{ color: '#9CA3AF' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#DC2626')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#9CA3AF')}
                  title="Archive"
                  aria-label={`Archive ${c.title || 'conversation'}`}
                  data-testid={`archive-conv-${c.id}`}
                >
                  <Archive className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        </aside>

        {/* RIGHT: thread + input */}
        <section className="relative flex min-w-0 flex-1 flex-col">
          <button
            type="button"
            onClick={() => setHistoryOpen(previous => !previous)}
            className="bsiq-focus-ring bsiq-icon-button absolute left-3 top-3 z-20 border border-[var(--bsiq-surface-border)] bg-white text-[var(--bsiq-text-primary)] shadow-sm"
            aria-label={historyOpen ? 'Hide conversation history' : 'Show conversation history'}
            aria-expanded={historyOpen}
          >
            {historyOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
          </button>
          {activeTab === 'approvals' ? (
            <div className="flex-1 overflow-y-auto bg-[#F5F1EC]" data-testid="atlas-review-view">
              <div className="mx-auto max-w-4xl px-6 py-8">
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-[#0D1F3C]">Review queue</h2>
                  <p className="mt-1 text-sm text-slate-600">Approve, edit, or reject actions Atlas prepared for you.</p>
                </div>
                <AtlasApprovalsList embedded onCountChange={setApprovalCount} />
              </div>
            </div>
          ) : <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="atlas-thread">
            {loadingConv && (
              <div className="flex items-center justify-center h-full text-sm" style={{ color: '#6B7280' }}>
                <Loader2 className="w-4 h-4 animate-spin mr-2" style={{ color: '#C49A00' }} /> Loading conversation...
              </div>
            )}
            {!loadingConv && messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full px-8 text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#0D1F3C' }}>
                  <MessageSquare className="w-6 h-6" style={{ color: '#C49A00' }} />
                </div>
                <h2 className="text-xl font-bold mb-2" style={{ color: '#0D1F3C' }}>Ask Atlas anything</h2>
                <p className="text-sm max-w-md mb-6" style={{ color: '#6B7280' }}>
                  Your federal BD capture workspace for opportunity intelligence,
                  capture decisions, amendments, forecasting, and approved communications.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full max-w-2xl">
                  {SUGGESTED_PROMPTS.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(p)}
                      data-testid={`suggested-${i}`}
                      className="bsiq-focus-ring flex min-h-11 items-start gap-2 rounded-lg px-4 py-3 text-left text-sm transition-colors"
                      style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', color: '#374151' }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#C49A00')}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#E5E7EB')}
                    >
                      <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: '#C49A00' }} />
                      <span>{p}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!loadingConv && messages.length > 0 && (
              <div className="mx-auto w-full max-w-4xl space-y-8 px-5 py-12 md:px-10">
                {messages.filter(m => m.role !== 'tool').map((m, i) => (
                  <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.role}-${i}`}>
                    {m.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: '#0D1F3C' }}>
                        <MessageSquare className="w-3.5 h-3.5" style={{ color: '#C49A00' }} />
                      </div>
                    )}
                    <div
                      className={m.role === 'user'
                        ? 'max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap md:max-w-[72%]'
                        : 'atlas-markdown min-w-0 flex-1 text-[15px] leading-7 text-[#111827]'}
                      style={m.role === 'user'
                        ? { backgroundColor: '#0D1F3C', color: '#FFFFFF', borderRadius: '12px 12px 4px 12px' }
                        : undefined
                      }
                    >
                      {m.role === 'assistant' ? <ReactMarkdown>{m.content}</ReactMarkdown> : m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex gap-3 justify-start" data-testid="thinking-indicator">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: '#0D1F3C' }}>
                      <MessageSquare className="w-3.5 h-3.5" style={{ color: '#C49A00' }} />
                    </div>
                    <div className="py-2 flex items-center gap-2 text-xs" style={{ color: '#6B7280' }}>
                      <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#C49A00' }} /> Atlas is thinking...
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Input bar */}
          <div className="p-4" style={{ borderTop: '1px solid #E5E7EB', backgroundColor: '#F5F1EC' }}>
            <div className="mx-auto max-w-4xl">
              <div
                className="flex items-end gap-2 px-3 py-2 transition-colors"
                style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px' }}
              >
                <textarea
                  ref={textareaRef}
                  data-testid="atlas-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                  }}
                  placeholder="Message Atlas..."
                  rows={1}
                  className="min-h-10 flex-1 resize-none bg-transparent py-2 text-sm leading-relaxed focus:outline-none max-h-40"
                  aria-label="Message Atlas"
                  style={{ color: '#111827' }}
                />
                <button
                  data-testid="atlas-send-btn"
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || sending}
                  className="bsiq-focus-ring bsiq-icon-button disabled:cursor-not-allowed disabled:opacity-40 transition-colors"
                  aria-label="Send message"
                  style={{ backgroundColor: '#0D1F3C', color: '#FFFFFF' }}
                  onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#162D56'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#0D1F3C'; }}
                  title="Send (Enter)"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-center mt-2" style={{ color: '#9CA3AF' }}>
                Atlas respects your autonomy policy. Sensitive actions are queued for approval.
              </p>
            </div>
          </div>
          </>}
        </section>
      </div>
  );

  if (embedded) return content;
  return <MainLayout title="Atlas" subtitle="Your BD capture operator" immersive>{content}</MainLayout>;
};

const AtlasChatPage = () => <AtlasChatInterface />;

export default AtlasChatPage;
